// What the search box chose, for the Plan map: a place to fly to and mark with a pin (spec §3.1). The top bar
// sets it; the Plan page shows it. Kept for the page only.
import { useSyncExternalStore } from 'react';
import type { OsmRef } from './placeDetails';

export interface SearchPin {
  /** What was found: a place's name, a grid reference, coordinates, or "Your location". */
  label: string;
  /** Where it is ("Gwynedd, United Kingdom"), or what kind of place it is. */
  detail?: string;
  /** OpenStreetMap's kind of place ("peak", "cafe"), for a place found by name. */
  kind?: string;
  /** The place in OpenStreetMap, for its website and phone (placeDetails.ts). */
  osm?: OsmRef;
  /** [lon, lat]. */
  at: [number, number];
  /** [west, south, east, north] for an area (a town or a region), to fit the map to. */
  extent?: [number, number, number, number];
  /** How close to zoom in for a point. */
  zoom: number;
  /** Changes with each search, so the map flies there again for the same place. */
  n: number;
}

let pin: SearchPin | null = null;
let count = 0;
const listeners = new Set<() => void>();
const set = (next: SearchPin | null) => {
  pin = next;
  for (const listener of listeners) listener();
};

export function showPlace(place: Omit<SearchPin, 'n'>) {
  set({ ...place, n: ++count });
}

export function clearPin() {
  set(null);
}

export function useSearchPin(): SearchPin | null {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => pin,
  );
}

/** How close to zoom for a kind of place: a summit or a hut closely, a village less so, a country far out. */
export function zoomFor(kind: string): number {
  if (/^(country|state|region|province)$/.test(kind)) return 6;
  if (/^(county|district|island|archipelago|national_park|protected_area)$/.test(kind)) return 9;
  if (/^(city|town|valley|nature_reserve|forest|wood|mountain_range|ridge|arete|water|lake|bay)$/.test(kind))
    return 12;
  if (/^(village|suburb|locality|glacier|reservoir)$/.test(kind)) return 14;
  return 15;
}
